/**
 * PersonelDurumParcalari.jsx — personel satırlarında iki ayrı "durum" bilgisinin PAYLAŞILAN parçaları.
 *
 *   · Kimlik doğrulama (`person.person_kimlik_dogrulama_durum`) — Daimî Liste satırlarında (backlog #111).
 *   · Eşleme durumu (`person_firma_to_fuar.durum`) — Fuar Kadrosu satırlarında, firmanın Saka'nın
 *     yaptığı eşlemeye cevabı (backlog #114 / backend #113).
 *
 * ⚠️ İkisi de "Bekliyor/Onaylandı/Reddedildi" gibi okunur ama TAMAMEN FARKLI şeylerdir ve ayrı
 * sütunlardır. Üstelik personel kartındaki sayaç yığını (`PersonelSayacStack`) BELGE sayısını gösterir.
 * Bu yüzden eşleme rozeti her yerde "Eşleme" / "Görevlendirme" sözcüğüyle birlikte basılır.
 *
 * Renkler `StandPills`in sabit hex paletinden (bu temada `success`/`secondary` sınıfları ters görünüyor,
 * bkz. KimlikDogrulamaModal) — Badge `color` prop'u DEĞİL, arka plan stili.
 */
import React, { useState } from "react";
import {
  Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, Nav, NavItem, NavLink,
  Card, CardHeader, CardBody,
} from "reactstrap";
import {
  ShieldCheck, ShieldX, ShieldQuestion, Clock, CheckCircle2, XCircle, Link2,
} from "lucide-react";
import { STATUS_HEX, NEUTRAL_GRAY } from "../Projects/StandPills";
import { ESLESME_DURUM, ESLESME_DURUM_IDS, RED_SEBEBI_MAX } from "../../services/personelRosterService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../i18n";

/** "2026-09-18 14:00:00" / ISO → "18.09.2026"; çözülemezse olduğu gibi. */
function tarihYaz(value) {
  if (!value) return null;
  const d = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString(currentLocaleTag());
}

// ─── Kimlik doğrulama ────────────────────────────────────────────────────────────────

const KIMLIK_ROZET = {
  0: { get label() { return i18n.t("components:personelDurum.dogrulanmadi"); }, hex: NEUTRAL_GRAY, Icon: ShieldQuestion },
  1: { get label() { return i18n.t("components:personelDurum.dogrulandi"); }, hex: STATUS_HEX.success, Icon: ShieldCheck },
  2: { get label() { return i18n.t("components:personelDurum.reddedildi"); }, hex: STATUS_HEX.danger, Icon: ShieldX },
  3: { get label() { return i18n.t("components:personelDurum.alandaDogrulanacak"); }, hex: STATUS_HEX.info, Icon: ShieldQuestion }, // #664
};

/** Tek satırlık kimlik doğrulama rozeti (liste satırı için; kart notlu geniş hali PersonelEkleModal'da). */
export function KimlikDogrulamaRozeti({ durum, className = "" }) {
  const meta = KIMLIK_ROZET[Number(durum ?? 0)] ?? KIMLIK_ROZET[0];
  const { Icon } = meta;
  return (
    <span
      className={`badge fw-normal d-inline-flex align-items-center gap-1 ${className}`}
      style={{ backgroundColor: meta.hex }}
    >
      <Icon size={12} />{meta.label}
    </span>
  );
}

// ─── Eşleme durumu ───────────────────────────────────────────────────────────────────

const ESLESME_GORUNUM = {
  0: { hex: STATUS_HEX.warning, Icon: Clock },
  1: { hex: STATUS_HEX.success, Icon: CheckCircle2 },
  2: { hex: STATUS_HEX.danger, Icon: XCircle },
};

/** `eslesmeDurumu` null (kaydı olmayan eski görevlendirme) → hiçbir şey basılmaz. */
export function EslesmeDurumRozeti({ durum, children, className = "" }) {
  const meta = ESLESME_GORUNUM[durum];
  if (meta == null) return null;
  const { Icon } = meta;
  return (
    <span
      className={`badge fw-normal d-inline-flex align-items-center gap-1 text-white ${className}`}
      style={{ backgroundColor: meta.hex }}
    >
      <Icon size={12} />{children ?? ESLESME_DURUM[durum].label}
    </span>
  );
}

/**
 * Kadro satırının eşleme özeti: rozet + kim/ne zaman + (varsa) firmanın kararı ve red sebebi.
 *
 * `record` hem ham `FirmaPersonelDTO` (denetmen) hem `mapPersonnelRecord` çıktısı (firma) olabilir —
 * `eslesme*` adları ikisinde de aynı. Eski görevlendirmede (`eslesmeDurumu` null) bir şey basılmaz.
 * `eslesmeRedSebebi` null olabilir; satır sebepsiz de düzgün okunur.
 */
export function EslesmeSatiri({ record, className = "" }) {
  const { t } = useTranslation("components");
  if (record?.eslesmeDurumu == null) return null;
  const tip = record.eslesmeTipi === 2 ? t("personelDurum.esleme") : t("personelDurum.gorevlendirme");
  const kim = record.eslesmeTipi === 2
    ? [record.eslesenAdi, tarihYaz(record.eslesmeTarihi)].filter(Boolean).join(" · ")
    : tarihYaz(record.eslesmeTarihi);
  const karar = record.eslesmeTipi === 2 && record.eslesmeDurumu !== 0
    ? [record.eslesmeKararVerenAdi, tarihYaz(record.eslesmeKararTarihi)].filter(Boolean).join(" · ")
    : null;

  return (
    <div className={`small ${className}`} style={{ whiteSpace: "normal" }}>
      <div className="d-flex align-items-center gap-1 flex-wrap">
        <EslesmeDurumRozeti durum={record.eslesmeDurumu}>{tip}: {ESLESME_DURUM[record.eslesmeDurumu].label}</EslesmeDurumRozeti>
        {kim && <span className="text-muted">{kim}</span>}
      </div>
      {karar && <div className="text-muted mt-1">{t("personelDurum.cevap")}{" "}{karar}</div>}
      {record.eslesmeDurumu === 2 && record.eslesmeRedSebebi && (
        <div className="text-danger fst-italic mt-1">“{record.eslesmeRedSebebi}”</div>
      )}
    </div>
  );
}

/**
 * Fuar Kadrosu durum sekmeleri — Tümü / Bekliyor / Onaylandı / Reddedildi.
 *
 * @param aktif   null (Tümü) | 0 | 1 | 2
 * @param sayilar zarfın `eslesmeDurumSayilari`'sı ({bekliyor, onaylandi, reddedildi, kayitYok}); null
 *                ise rozet basılmaz. "Tümü" dört sayının toplamıdır — kaydı olmayan eski satırlar
 *                yalnızca orada çıktığı için `kayitYok` buraya dahildir.
 */
export function EslesmeSekmeleri({ aktif, onChange, sayilar }) {
  const { t } = useTranslation("components");
  const adet = {
    0: sayilar?.bekliyor, 1: sayilar?.onaylandi, 2: sayilar?.reddedildi,
  };
  const tumu = sayilar
    ? (sayilar.bekliyor ?? 0) + (sayilar.onaylandi ?? 0) + (sayilar.reddedildi ?? 0) + (sayilar.kayitYok ?? 0)
    : null;
  const sekmeler = [
    { key: "tumu", value: null, label: t("common:tumu"), count: tumu, hex: NEUTRAL_GRAY },
    ...ESLESME_DURUM_IDS.map((id) => ({
      key: String(id), value: id, label: ESLESME_DURUM[id].label, count: adet[id], hex: ESLESME_GORUNUM[id].hex,
    })),
  ];

  return (
    <Nav pills className="mb-0">
      {sekmeler.map((s) => {
        const secili = aktif === s.value;
        return (
          <NavItem key={s.key}>
            <NavLink
              href="#"
              className={secili ? "active" : ""}
              onClick={(e) => { e.preventDefault(); onChange(s.value); }}
            >
              {s.label}
              {s.count != null && (
                <span
                  className={`badge ms-1 ${secili ? "bg-light text-body" : "text-white"}`}
                  style={secili ? undefined : { backgroundColor: s.hex }}
                >
                  {s.count}
                </span>
              )}
            </NavLink>
          </NavItem>
        );
      })}
    </Nav>
  );
}

/**
 * Firma tarafı cevap düğmeleri. YALNIZCA Eşleme satırı (`eslesmeTipi` 2) cevaplanır — firmanın kendi
 * Görevlendirme'si ve kaydı olmayan eski satırın cevaplanacak bir şeyi yok (backend 400 verir).
 * Cevap her zaman değiştirilebilir: Onaylandı satırında yalnızca Reddet, Reddedildi satırında yalnızca
 * Onayla, Bekliyor'da ikisi de görünür.
 */
export function EslesmeCevapDugmeleri({ record, busy, onOnayla, onReddet }) {
  const { t } = useTranslation("components");
  if (record?.eslesmeTipi !== 2) return null;
  const d = record.eslesmeDurumu;
  return (
    // `flex-fill`: tek düğme kalsa (Onaylıda yalnızca Reddet, Reddedilmişte yalnızca Onayla) da sütunu
    // doldurur — kartın sağ düğme sütunu hep aynı genişlikte (bkz. PersonelRosterEylemleri.KART_EYLEM_SUTUNU).
    <div className="d-flex gap-1">
      {d !== 1 && (
        <Button size="sm" color="success" className="flex-fill" disabled={busy} onClick={() => onOnayla(record)}>
          <CheckCircle2 size={14} className="me-1" />{t("personelDurum.onayla")}
        </Button>
      )}
      {d !== 2 && (
        <Button size="sm" color="danger" outline className="flex-fill" disabled={busy} onClick={() => onReddet(record)}>
          <XCircle size={14} className="me-1" />{t("personelDurum.reddet")}
        </Button>
      )}
    </div>
  );
}

/** Red sebebi sorusu — sebep ZORUNLU (en fazla RED_SEBEBI_MAX); onay için modal gerekmez. */
export function EslesmeRedModal({ adSoyad, busy, onClose, onConfirm }) {
  const { t } = useTranslation("components");
  const [sebep, setSebep] = useState("");
  const temiz = sebep.trim();
  const uzun = temiz.length > RED_SEBEBI_MAX;
  return (
    <Modal isOpen toggle={() => !busy && onClose()}>
      <ModalHeader toggle={() => !busy && onClose()}>{t("personelDurum.eslemeyiReddet")}</ModalHeader>
      <ModalBody>
        <p className="mb-3">
          <strong>{adSoyad}</strong>{" "}{t("personelDurum.firmanizUzerindenFuardaGirisKarti")}
        </p>
        <Label className="small mb-1">{t("personelDurum.sebep")}{" "}<span className="text-danger">*</span></Label>
        <Input
          type="textarea" rows={3} value={sebep} autoFocus
          maxLength={RED_SEBEBI_MAX + 50} invalid={uzun}
          onChange={(e) => setSebep(e.target.value)}
        />
        <div className={`small mt-1 ${uzun ? "text-danger" : "text-muted"}`}>
          {temiz.length} / {RED_SEBEBI_MAX}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={busy}>{t("personelDurum.vazgec")}</Button>
        <Button color="danger" onClick={() => onConfirm(temiz)} disabled={busy || !temiz || uzun}>
          {t("personelDurum.reddet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Detay sayfalarının "Eşleme" kartı — satır özeti + cevap geçmişi (`eslesmeGecmisi[]`, en yeni önce).
 * Eski (kaydı olmayan) görevlendirmede hiçbir şey basılmaz. Salt okunur: cevap firma portalının
 * Fuar Kadrosu listesinden verilir.
 */
export function EslesmeKarti({ record }) {
  const { t } = useTranslation("components");
  if (record?.eslesmeDurumu == null) return null;
  const gecmis = Array.isArray(record.eslesmeGecmisi) ? record.eslesmeGecmisi : [];
  return (
    <Card className="mb-3">
      <CardHeader className="bg-transparent d-flex align-items-center gap-2">
        <Link2 size={16} className="text-muted" />
        <span className="fw-semibold">{t("personelDurum.esleme")}</span>
      </CardHeader>
      <CardBody>
        <EslesmeSatiri record={record} />
        {gecmis.length > 0 && (
          <div className="mt-3">
            <div className="text-uppercase text-muted fw-semibold small mb-2">{t("personelDurum.cevapGecmisi")}</div>
            <div className="vstack gap-2">
              {gecmis.map((g, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="small">
                  <EslesmeDurumRozeti durum={g.durum}>{g.durumAdi ?? ESLESME_DURUM[g.durum]?.label}</EslesmeDurumRozeti>
                  <span className="text-muted ms-2">
                    {[g.kararVerenAdi, tarihYaz(g.kararTarihi)].filter(Boolean).join(" · ")}
                    {g.islem === 2 ? t("personelDurum.gorevlendirerekKabulEtti") : ""}
                  </span>
                  {g.redSebebi && <div className="text-danger fst-italic mt-1">“{g.redSebebi}”</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
